<template>
  <section>
    <div class="flex items-center justify-between mb-4">
      <h4 class="mb-2">
        {{ $t("stats.course.sidebar.students") }}
      </h4>
      <Button
        :label="$t('labels.generic', { pt: 'Baixar', en: 'Download' })"
        icon="pi pi-download"
        class="p-button-sm btn-primary h-9"
        :loading="btnLoading"
        @click="downloadCSV"
      />
    </div>
    <div class="bg-white rounded-lg border border-gray-300">
      <Table
        :slotData="students"
        :columns="columnsData"
        :actions="actions"
        :loading="loading"
        :filters="filters"
        :params="params"
        :hasStatus="true"
        :hasActions="true"
        :actionsTitle="$t('labels.generic', { pt: 'Detalhes', en: 'Details' })"
        @onFilter="filterFor()"
        @onGoTo="(e) => goTo(e)"
        @onClearFilter="clearFilter()"
        @onToggleFilter="(e, type) => toggleFilter(e, type)"
        @onPaginate="paginate($event)"
      >
        <template #filters>
          <InputText v-if="filterType == 'userName'" v-model="filters.name" />
        </template>
      </Table>
    </div>
  </section>
</template>

<script lang="ts">
import { QueryParams } from "@/models/query-params.model";
import { StatsRest } from "@/services/rest/stats.rest";
import { GoToRoute } from "@/utils/go-to.util";
import { saveAs } from "file-saver";
import { debounceTime, delay } from "rxjs";
import { defineComponent } from "vue";
import { StatsService } from "../../stats.service";
import { StudentsService } from "./students.service";
import { columns } from "./students.utils";

export default defineComponent({
  name: "students",
  computed: {
    rest(): StatsRest {
      return new StatsRest();
    },
    service(): StudentsService {
      return new StudentsService();
    },
    go(): GoToRoute {
      return new GoToRoute();
    },
  },
  data() {
    return {
      courseId: this.$route.params.courseId as string,
      columnsData: columns as any,
      loading: true as boolean,
      btnLoading: false as boolean,
      students: [] as any,
      actions: [
        {
          title: "Ver aluno",
          icon: "hi-chevron-right",
          type: "goTo",
          style: "btn-outline",
        },
      ] as any,
      params: new QueryParams(),
      filters: {
        isCleareable: false,
        name: null,
      } as any,
      filterType: "" as string,
      classroomId: undefined as string | undefined,
      hasClassroom: false as boolean | undefined,
    };
  },
  mounted() {
    this.studentsPaginated();
    this.getClassroomId();
  },
  watch: {
    classroomId(val, old) {
      if (val !== undefined || val !== old) this.studentsPaginated();
    },
  },
  methods: {
    goTo(data: any): void {
      this.go.redirectToPage("statsStudent", {
        studentId: data.userId,
        courseId: this.courseId,
        studentName: data.userName,
      });

      if (data.classroomId) {
        localStorage.setItem("stdClassroomId", data.classroomId);
        localStorage.setItem(
          "stdClassroomName",
          JSON.stringify(data.classroomName)
        );
      } else {
        localStorage.removeItem("stdClassroomId");
        localStorage.removeItem("stdClassroomName");
      }
    },
    paginate(event: { page: number; rows: number }): void {
      const params = new QueryParams(event.page, event.rows);
      params.name = this.filters.name;
      this.setPage(params);
    },
    setPage(queryParams: QueryParams): void {
      this.loading = true;
      queryParams.classroomId = this.classroomId ?? undefined;

      this.service.students.pipe(debounceTime(500)).subscribe({
        next: (response: any) => {
          this.loading = false;
          this.students = response.content;
          this.params.total = response.total;
        },
        error: () => {
          this.loading = false;
        },
      });
      this.service.getStudentsFromCourse(this.courseId, queryParams);
    },
    studentsPaginated(): void {
      const page = this.params.page;
      const size = this.params.size;
      const params = new QueryParams(page, size);
      this.setPage(params);
    },
    toggleFilter(event: any, type: string): void {
      this.filterType = type;
    },
    filterFor(): void {
      this.filters.isCleareable = this.isCleareable();
      var params = new QueryParams();
      params.name = this.filters.name;
      if (this.filterType == "userName") {
        this.setPage(params);
      }
    },
    clearFilter(): void {
      this.filters.isCleareable = false;
      this.filters.name = null;
      this.studentsPaginated();
    },
    isCleareable(): boolean {
      return this.filters.name !== "";
    },
    downloadCSV(): void {
      this.btnLoading = true;
      this.rest
        .getStudentsStatsCsv(this.courseId, {
          classroomId: this.classroomId ?? undefined,
        })
        .pipe(delay(1000))
        .subscribe({
          next: (response) => {
            this.btnLoading = false;
            const blob = new Blob([response], { type: "text/csv" });
            const url = window.URL.createObjectURL(blob);
            saveAs(url, `students_data.csv`);
          },
          error: () => {
            this.btnLoading = false;
          },
        });
    },
    getClassroomId(): void {
      this.loading = true;
      StatsService.classroom.subscribe({
        next: (e) => (this.classroomId = e),
      });
    },
  },
});
</script>
